// src/components/common/aia/AIAvatarContainer.js
import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAIAvatar } from './AIAvatarContext';
import { ChatProvider } from './context/ChatContext';
import { getAutoStartTour, getTourById } from './tours';
import AIAvatar from './AIAvatar';
import FTUEPanel from './FTUEPanel';
import TourHighlight from './TourHighlight';
import { useAuth } from '../../../hooks/useAuth';
import employeeService from '../../../services/employee.service';

const getNudgeLaterKey = (userId) => `oakhr_ftue_nudge_later_${userId || 'unknown'}`;
const GLOBAL_NUDGE_LATER_KEY = 'oakhr_ftue_nudge_later';
const FTUE_DISMISSED_KEY = 'oakhr_ftue_dismissed';
const SESSION_NUDGE_LATER_KEY = 'oakhr_ftue_nudge_later_session';

const AIAvatarContainer = () => {
    const { user, company } = useAuth();
    const {
        isOpen,
        currentTour,
        tourStep,
        userRole,
        setUserRole,
        startTour,
        nextTourStep,
        goToTourStep,
        previousTourStep,
        skipTour,
        completeTour,
        isTourCompleted,
        getResumeData,
        clearResumeData,
    } = useAIAvatar();

    const [showBanner, setShowBanner] = useState(false);
    const [bannerDismissed, setBannerDismissed] = useState(() => {
        return (
            localStorage.getItem(GLOBAL_NUDGE_LATER_KEY) === 'true' ||
            localStorage.getItem(FTUE_DISMISSED_KEY) === 'true' ||
            sessionStorage.getItem(SESSION_NUDGE_LATER_KEY) === 'true'
        );
    });
    const [stepTargetClicked, setStepTargetClicked] = useState(false);
    const [stepTargetValueReady, setStepTargetValueReady] = useState(false);
    const [showTourClosedHint, setShowTourClosedHint] = useState(false);
    const [lastSkippedTour, setLastSkippedTour] = useState(null);

    const resolveUserRole = (currentUser) => {
        if (!currentUser) return 'employee';
        const role = (currentUser.role || '').toLowerCase();
        const roleName = (currentUser.role_name || '').toLowerCase();
        const accessLevel = (currentUser.access_level || '').toLowerCase();

        if (
            currentUser.is_admin ||
            accessLevel === 'admin' ||
            role === 'admin' ||
            roleName.includes('admin')
        ) {
            return 'admin';
        }

        if (role === 'manager' || roleName.includes('manager')) {
            return 'manager';
        }

        return 'employee';
    };

    const getNudgeUserKey = (currentUser) => {
        return (
            currentUser?.id ||
            currentUser?.employee_id ||
            currentUser?.email ||
            currentUser?.work_email ||
            'unknown'
        );
    };

    // Set user role from auth and conditionally show nudge
    useEffect(() => {
        if (!user) {
            return;
        }

        let timer = null;
        let active = true;

        const maybeShowNudge = async () => {
            const role = resolveUserRole(user);
            setUserRole(role);

            const autoStartTour = getAutoStartTour(role);
            if (!autoStartTour) {
                return;
            }

            // Never show nudge after first tour completion
            if (isTourCompleted(autoStartTour.id)) {
                return;
            }

            if (bannerDismissed) {
                return;
            }

            // "Later" should persist across refresh
            if (
                sessionStorage.getItem(SESSION_NUDGE_LATER_KEY) === 'true' ||
                localStorage.getItem(GLOBAL_NUDGE_LATER_KEY) === 'true' ||
                localStorage.getItem(getNudgeLaterKey(getNudgeUserKey(user))) === 'true'
            ) {
                return;
            }

            // Do not show for companies with more than 2 employees
            let employeeCount =
                Number(company?.employee_count) ||
                Number(company?.employeeCount) ||
                Number(company?.total_employees) ||
                Number(company?.employees_count) ||
                null;

            if (employeeCount === null) {
                try {
                    const employees = await employeeService.getAllEmployees();
                    employeeCount = Array.isArray(employees) ? employees.length : 0;
                } catch (error) {
                    employeeCount = 0;
                }
            }

            if (employeeCount > 2) {
                return;
            }

            timer = setTimeout(() => {
                if (active) {
                    setShowBanner(true);
                }
            }, 2000);
        };

        maybeShowNudge();

        return () => {
            active = false;
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [user, company, setUserRole, isTourCompleted, bannerDismissed]);

    // Get current tour data
    const tour = currentTour ? getTourById(currentTour, userRole) : null;
    const currentStep = tour?.steps[tourStep];

    const getStepSection = (step, tourId) => {
        if (!step) {
            return { id: 'general', label: 'General' };
        }

        if (step.section) {
            const explicit = String(step.section).toLowerCase();
            const labels = {
                dashboard: 'Dashboard',
                vibe: 'Vibe',
                org: 'Org View',
                employees: 'Employees',
                attendance: 'Attendance',
                approvals: 'Approvals',
                payroll: 'Payroll',
                documents: 'Documents'
            };
            return { id: explicit, label: labels[explicit] || 'General' };
        }

        const target = step.target || '';
        if (tourId === 'admin-welcome-tour') {
            if (target === '.tour-admin-dashboard-vibe-section') return { id: 'vibe', label: 'Vibe' };
            if (!step.target || target === '.tour-dashboard' || target.startsWith('.tour-admin-dashboard')) return { id: 'dashboard', label: 'Dashboard' };
            if (target.startsWith('.tour-vibe-')) return { id: 'vibe', label: 'Vibe' };
            if (
                target === '.tour-org-view' ||
                target.startsWith('.tour-functional-structure') ||
                target.startsWith('.tour-organization-structure') ||
                target.startsWith('.tour-legal-') ||
                target === '.tour-add-legal-entity' ||
                target === '.tour-save-legal-entity'
            ) {
                return { id: 'org', label: 'Org View' };
            }
            if (target === '.tour-employees' || target.startsWith('.tour-employee-')) return { id: 'employees', label: 'Employees' };
            if (target === '.tour-attendance' || target.startsWith('.tour-attendance-')) return { id: 'attendance', label: 'Attendance' };
            if (target === '.tour-approvals') return { id: 'approvals', label: 'Approvals' };
            if (target === '.tour-payrollrun') return { id: 'payroll', label: 'Payroll' };
            if (target === '.tour-document-builder') return { id: 'documents', label: 'Documents' };
            return { id: 'completion', label: 'Completion' };
        }

        return { id: 'general', label: 'General' };
    };

    const sectionInfo = (() => {
        if (!tour || !tour.steps?.length) {
            return {
                label: 'General',
                stepNumber: 1,
                stepCount: 1,
                progress: 100,
                nextSectionStart: null
            };
        }

        const sections = tour.steps.map(step => getStepSection(step, tour.id));
        const currentSection = sections[tourStep] || sections[0];
        const sectionIndexes = [];
        sections.forEach((section, index) => {
            if (section.id === currentSection.id) {
                sectionIndexes.push(index);
            }
        });

        const currentPosition = Math.max(0, sectionIndexes.indexOf(tourStep));
        const nextSectionStart = sections.findIndex((section, index) => index > tourStep && section.id !== currentSection.id);

        return {
            label: currentSection.label,
            stepNumber: currentPosition + 1,
            stepCount: sectionIndexes.length || 1,
            progress: ((currentPosition + 1) / (sectionIndexes.length || 1)) * 100,
            nextSectionStart: nextSectionStart >= 0 ? nextSectionStart : null
        };
    })();
    const hasConditionalTarget = !!(
        currentStep?.requireClickIfMissingTarget &&
        document.querySelector(currentStep.requireClickIfMissingTarget)
    );
    const requiresClickBecauseMissing = !!(
        currentStep?.requireClickIfMissingTarget &&
        !document.querySelector(currentStep.requireClickIfMissingTarget)
    );
    const isClickRequiredForStep = (!!currentStep?.requireClick && !hasConditionalTarget) || requiresClickBecauseMissing;
    const requiresTargetValue = !!currentStep?.requireTargetValue;
    const canProceedClick = !isClickRequiredForStep || stepTargetClicked;
    const canProceedValue = !requiresTargetValue || stepTargetValueReady;
    const canProceed = canProceedClick && canProceedValue;
    const shouldSkipCurrentStep = !!(
        currentStep &&
        (
            // If a legal entity already exists, skip the "Add Legal Entity" step.
            (currentStep.requireClickIfMissingTarget && document.querySelector(currentStep.requireClickIfMissingTarget)) ||
            // If input step target is not rendered, skip input-required step.
            (currentStep.requireTargetValue && (!currentStep.target || !document.querySelector(currentStep.target)))
        )
    );

    useEffect(() => {
        setStepTargetClicked(false);
        setStepTargetValueReady(false);
    }, [currentTour, tourStep]);

    useEffect(() => {
        if (!tour || !currentStep || !shouldSkipCurrentStep) {
            return;
        }
        if (tourStep < tour.steps.length - 1) {
            const timer = setTimeout(() => {
                nextTourStep();
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [tour, currentStep, shouldSkipCurrentStep, tourStep, nextTourStep]);

    useEffect(() => {
        if (!tour || !currentStep) {
            return;
        }

        const isMandatoryStep = isClickRequiredForStep || requiresTargetValue;
        const hasNextStep = tourStep < tour.steps.length - 1;

        if (!isMandatoryStep || !canProceed || !hasNextStep) {
            return;
        }

        const timer = setTimeout(() => {
            nextTourStep();
        }, 120);

        return () => clearTimeout(timer);
    }, [
        tour,
        currentStep,
        tourStep,
        canProceed,
        isClickRequiredForStep,
        requiresTargetValue,
        nextTourStep
    ]);

    useEffect(() => {
        if (!isClickRequiredForStep || !currentStep?.target || !currentTour) {
            return;
        }

        const handleStepTargetClick = (event) => {
            if (event.target.closest(currentStep.target)) {
                setStepTargetClicked(true);
            }
        };

        document.addEventListener('click', handleStepTargetClick, true);
        return () => {
            document.removeEventListener('click', handleStepTargetClick, true);
        };
    }, [currentStep, currentTour, isClickRequiredForStep]);

    useEffect(() => {
        if (!requiresTargetValue || !currentStep?.target || !currentTour) {
            return;
        }

        const evaluateTargetValue = () => {
            const input = document.querySelector(currentStep.target);
            if (!input) {
                setStepTargetValueReady(false);
                return;
            }
            const value = typeof input.value === 'string' ? input.value.trim() : '';
            setStepTargetValueReady(value.length > 0);
        };

        const handleInput = (event) => {
            if (event.target.closest(currentStep.target)) {
                evaluateTargetValue();
            }
        };

        evaluateTargetValue();
        document.addEventListener('input', handleInput, true);
        return () => {
            document.removeEventListener('input', handleInput, true);
        };
    }, [currentStep, currentTour, requiresTargetValue]);

    // Keyboard navigation for tours
    useEffect(() => {
        if (!currentTour || !tour) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                handleSkip();
            } else if ((e.key === 'ArrowRight' || e.key === 'Enter') && canProceed) {
                e.preventDefault();
                if (tourStep < tour.steps.length - 1) {
                    nextTourStep();
                } else {
                    completeTour(tour.id);
                }
            } else if (e.key === 'ArrowLeft' && tourStep > 0) {
                e.preventDefault();
                previousTourStep();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [currentTour, tour, tourStep, canProceed, nextTourStep, previousTourStep, completeTour]);

    const handleNext = () => {
        if (!canProceed) {
            return;
        }
        if (tour && tourStep < tour.steps.length - 1) {
            nextTourStep();
        }
    };

    const handlePrevious = () => {
        if (tourStep > 0) {
            previousTourStep();
        }
    };

    const handleSkip = () => {
        // Save info for resume toast before clearing
        if (currentTour && tour) {
            setLastSkippedTour({ tourId: currentTour, step: tourStep, tourTitle: tour.title });
        }
        skipTour();
        setShowTourClosedHint(true);
    };

    const handleResumeTour = () => {
        const resumeData = getResumeData();
        if (resumeData) {
            startTour(resumeData.tourId, resumeData.lastStep);
            clearResumeData();
        }
        setShowTourClosedHint(false);
        setLastSkippedTour(null);
    };

    const handleSkipSection = () => {
        if (sectionInfo.nextSectionStart !== null) {
            goToTourStep(sectionInfo.nextSectionStart);
        }
    };

    const handleComplete = () => {
        if (tour) {
            completeTour(tour.id);
        }
    };

    const handleStartTour = () => {
        const autoStartTour = getAutoStartTour(userRole);
        if (autoStartTour) {
            startTour(autoStartTour.id);
            setShowBanner(false);
        }
    };

    const handleDismissBanner = () => {
        setShowBanner(false);
        setBannerDismissed(true);
    };

    const handleLaterBanner = () => {
        sessionStorage.setItem(SESSION_NUDGE_LATER_KEY, 'true');
        localStorage.setItem(GLOBAL_NUDGE_LATER_KEY, 'true');
        localStorage.setItem(getNudgeLaterKey(getNudgeUserKey(user)), 'true');
        setShowBanner(false);
        setBannerDismissed(true);
    };

    const highlightTarget = currentStep?.target;

    useEffect(() => {
        if (!showTourClosedHint) {
            return;
        }

        const timer = setTimeout(() => {
            setShowTourClosedHint(false);
        }, 5000);

        return () => clearTimeout(timer);
    }, [showTourClosedHint]);

    return (
        <ChatProvider>
            <>
            {/* FTUE Welcome Banner */}
            <AnimatePresence>
                {showBanner && !currentTour && (
                    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[9999] px-4">
                        <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl shadow-2xl p-1 max-w-lg">
                            <div className="bg-white dark:bg-gray-800 rounded-xl px-5 py-4 flex items-center gap-4">
                                {/* Avatar icon */}
                                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                                    <span className="text-2xl">🤖</span>
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold text-gray-900 dark:text-white">
                                        New to OakHR?
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Take a quick tour to get started!
                                    </p>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                        onClick={handleLaterBanner}
                                        className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
                                    >
                                        Later
                                    </button>
                                    <button
                                        onClick={handleDismissBanner}
                                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                        title="Skip"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={handleStartTour}
                                        className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg text-sm font-medium transition-all shadow-lg"
                                    >
                                        Start Tour
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </AnimatePresence>

            {/* Highlight overlay */}
            {currentTour && highlightTarget && (
                <TourHighlight
                    target={highlightTarget}
                    position={currentStep?.position || 'bottom'}
                    active={!!currentTour}
                    interactive={isClickRequiredForStep}
                    onOverlayClick={handleSkip}
                />
            )}

            {/* FTUE Panel */}
            <AnimatePresence>
                {isOpen && tour && (
                    <FTUEPanel
                        tour={tour}
                        currentStep={tourStep}
                        canProceed={canProceed}
                        requireClick={isClickRequiredForStep}
                        requireValue={requiresTargetValue}
                        sectionLabel={sectionInfo.label}
                        sectionStepNumber={sectionInfo.stepNumber}
                        sectionStepCount={sectionInfo.stepCount}
                        sectionProgress={sectionInfo.progress}
                        canSkipSection={sectionInfo.nextSectionStart !== null}
                        inlinePanel={!!currentStep?.inlinePanel}
                        targetSelector={currentStep?.target}
                        panelPosition={currentStep?.position || 'bottom'}
                        onNext={handleNext}
                        onPrevious={handlePrevious}
                        onSkipSection={handleSkipSection}
                        onSkip={handleSkip}
                        onComplete={handleComplete}
                    />
                )}
            </AnimatePresence>

            {/* Avatar Button */}
            <AIAvatar />

            {/* Tour Closed Hint */}
            <AnimatePresence>
                {showTourClosedHint && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                        className="fixed bottom-28 right-6 z-[10000]"
                    >
                        <div className="rounded-xl shadow-lg border px-4 py-3 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 flex items-center gap-3">
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                Tour paused.
                            </span>
                            {lastSkippedTour && (
                                <button
                                    onClick={handleResumeTour}
                                    className="px-3 py-1 text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors"
                                >
                                    Resume
                                </button>
                            )}
                            <button
                                onClick={() => { setShowTourClosedHint(false); setLastSkippedTour(null); }}
                                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
        </ChatProvider>
    );
};

export default AIAvatarContainer;
